import { Vector3 } from "three";
import { GroupState } from "../ik/group/group";

export interface Boxscore {
  teams: {
    away: {
      team: {
        id: number;
        abbreviation: string;
      };
      players: Players;
    };
    home: {
      team: {
        id: number;
        abbreviation: string;
        venue: {
          id: number;
        };
      };
      players: Players;
    };
  };
}

export interface Players {
  [playerId: string]: Player;
}

export interface Player {
  person: {
    id: number;
    boxscoreName: string;
    useName: string;
    nameFirstLast: string;
    lastName: string;
    initLastName: string;
    pitchHand: Description;
    primaryPosition: PrimaryPostion;
    batSide: Description;
  };
  jerseyNumber: string;
  parentTeamId: number;
}

// taken from:
// https://github.mlbam.net/baseball-data/binapi-statcast/blob/master/src/main/java/net/mlbam/statcast/enums/PlayEventType.java
export enum PlayEventType {
  BEGIN_OF_PLAY = 0,
  PITCHER_FIRST_MOVEMENT = 1,

  BALL_WAS_PITCHED = 2,
  BALL_WAS_HIT = 3,
  BALL_WAS_CAUGHT = 4,
  BALL_WAS_CAUGHT_OUT = 5,
  BALL_WAS_RELEASED = 6,
  BALL_WAS_DEFLECTED = 7,

  TAG_WAS_APPLIED = 8,
  PICK_OFF_BALL_RELEASED = 9,
  END_OF_PLAY = 10,
  REMOVED = 11,

  // assume these are not used:
  TRACKMAN_BALL_WAS_RELEASED = 12,
  TRACKMAN_BALL_WAS_DEFLECTED = 13,
  TRACKMAN_BALL_WAS_CATCHER_RELEASED = 14,
  TRACKMAN_PICK_OFF_BALL_RELEASED = 15,
  TRACKMAN_BALL_BOUNCE = 16,

  BALL_WAS_FIELDED = 17,
  UNKNOWN = 18,
  BALL_BOUNCE = 19,

  OPERATOR_INPUT_NOT_FOUND = 22,
  OFF_THE_WALL = 23,
}

export const playEventTypeMap: {
  [key in PlayEventType]: string;
} = {
  [PlayEventType.BEGIN_OF_PLAY]: "BEGIN_OF_PLAY",
  [PlayEventType.PITCHER_FIRST_MOVEMENT]: "PITCHER_FIRST_MOVEMENT",
  [PlayEventType.BALL_WAS_PITCHED]: "BALL_WAS_PITCHED",
  [PlayEventType.BALL_WAS_HIT]: "BALL_WAS_HIT",
  [PlayEventType.BALL_WAS_CAUGHT]: "BALL_WAS_CAUGHT",
  [PlayEventType.BALL_WAS_CAUGHT_OUT]: "BALL_WAS_CAUGHT_OUT",
  [PlayEventType.BALL_WAS_RELEASED]: "BALL_WAS_RELEASED",
  [PlayEventType.BALL_WAS_DEFLECTED]: "BALL_WAS_DEFLECTED",
  [PlayEventType.TAG_WAS_APPLIED]: "TAG_WAS_APPLIED",
  [PlayEventType.PICK_OFF_BALL_RELEASED]: "PICK_OFF_BALL_RELEASED",
  [PlayEventType.END_OF_PLAY]: "END_OF_PLAY",
  [PlayEventType.REMOVED]: "REMOVED",
  [PlayEventType.TRACKMAN_BALL_WAS_RELEASED]: "TRACKMAN_BALL_WAS_RELEASED",
  [PlayEventType.TRACKMAN_BALL_WAS_DEFLECTED]: "TRACKMAN_BALL_WAS_DEFLECTED",
  [PlayEventType.TRACKMAN_BALL_WAS_CATCHER_RELEASED]:
    "TRACKMAN_BALL_WAS_CATCHER_RELEASED",
  [PlayEventType.TRACKMAN_PICK_OFF_BALL_RELEASED]:
    "TRACKMAN_PICK_OFF_BALL_RELEASED",
  [PlayEventType.TRACKMAN_BALL_BOUNCE]: "TRACKMAN_BALL_BOUNCE",
  [PlayEventType.BALL_WAS_FIELDED]: "BALL_WAS_FIELDED",
  [PlayEventType.UNKNOWN]: "UNKNOWN",
  [PlayEventType.BALL_BOUNCE]: "BALL_BOUNCE",
  [PlayEventType.OPERATOR_INPUT_NOT_FOUND]: "OPERATOR_INPUT_NOT_FOUND",
  [PlayEventType.OFF_THE_WALL]: "OFF_THE_WALL",
};

export interface TrackedEvent {
  timestamp: string;
  batSide: string;
  pitchHand: string;
  atBatNumber: number;
  pitchNumber: number;
  pickoffNumber: number;
  szTop: number;
  szBot: number;
  eventType: string;
  x: number;
  y: number;
  z: number;
  position: number;
  eventTypeId: PlayEventType;
}

export interface Coordinate {
  x: number;
  y: number;
  z: number;
}

export interface Position {
  x: number;
  y: number;
  z?: number;
  timeStamp: string;
}

interface Description {
  code: string;
  description: string;
}

interface PrimaryPostion {
  code: number;
  name: string;
  type: string;
  abbreviation: string;
}

// TODO: do we want to add the other accumulated data to this interface
//       (jointsMap: JointsMapType for example)
export interface SkeletalData {
  skeletalData: {
    frames: SkeletalFrame[];
    startTime: number;
    endTime: number;
  };
}

export interface SkeletalStructure {
  [key: string]: string[];
}

export interface PositionXYZ {
  x: number;
  y: number;
  z: number;
}

export interface InferredBatPosition {
  head_position: PositionXYZ;
  handle_position: PositionXYZ;
}

export interface SkeletalFrame {
  positions: SkeletalPosition[];
  ballPosition?: PositionXYZ;
  trackedEvents?: TrackedEvent[];
  inferredBat?: InferredBatPosition;
  num: number;
  time: number;
  timeStamp: string;
}

export interface SkeletalPose {
  state: GroupState;
  centroid: Vector3;
  time?: number;
}

// TODO: we should make this a Map
export interface SkeletalPosition {
  positionId: number;
  track_id: number;
  joints: {
    id: number;
    x: number;
    y: number;
    z: number;
  }[];

  posed?: SkeletalPose;
}

export interface RawManifest {
  manifestRecords: RawRecord[];
}

interface RawRecord {
  fileName: string;
  manifestDataContent: {
    startTimestamp: string;
  };
}

interface GumboCoords {
  x: number;
  y: number;
  x0: number;
  y0: number;
  z0: number;
  vX0: number;
  vY0: number;
  vZ0: number;
  aX: number;
  aY: number;
  aZ: number;
  pX: number;
  pZ: number;
  pfxX: number;
  pfxZ: number;
}

export interface Play {
  pitchIndex: number[];
  about: {
    atBatIndex: number;
    isComplete: boolean;
  };
  count: {
    balls: number;
    strikes: number;
    outs: number;
  };
  matchup: {
    batSide: Description;
    pitchHand: Description;
    batter: {
      id: number;
      fullName: string;
    };
    pitcher: {
      id: number;
      fullName: string;
    };
    postOnFirst?: {
      id: number;
      fullName: string;
    };
    postOnSecond?: {
      id: number;
      fullName: string;
    };
    postOnThird?: {
      id: number;
      fullName: string;
    };
  };
  playEvents: {
    playId: string;
    isPitch: boolean;
    pitchData: {
      plateTime: number;
      coordinates: GumboCoords;
      strikeZoneTop: number;
      strikeZoneBottom: number;
    };
    hitData: {
      launchSpeed: number;
      launchAngle: number;
      totalDistance: number;
      trajectory: string;
      hardness: string;
      location: number;
      coordinates: {
        coordX: number;
        coordY: number;
      };
    };
    startTime: string;
    endTime: string;
  }[];
}

export interface Gumbo {
  gamePk: number;
  metaData: {
    timeStamp: string;
  };
  gameData: {
    game: {
      pk: string;
    };
    datetime: {
      dateTime: string;
    };
    status: {
      abstractGameState: string;
      abstractGameCode: string;
    };
    teams: {
      away: {
        id: number;
        abbreviation: string;
      };
      home: {
        id: number;
        abbreviation: string;
        venue: Venue;
      };
    };
    venue: Venue;
  };
  liveData: {
    linescore: {
      currentInning: number;
      inningHalf: string;
      inningState: string;
      offense: {
        team: {
          id: number;
        };
        batter: {
          id: number;
        };
        first: {
          id: number;
          fullName: string;
        };
        second: {
          id: number;
          fullName: string;
        };
        third: {
          id: number;
          fullName: string;
        };
      };
      defense: {
        catcher: {
          id: number;
          fullName: string;
        };
        first: {
          id: number;
          fullName: string;
        };
        second: {
          id: number;
          fullName: string;
        };
        third: {
          id: number;
          fullName: string;
        };
        shortstop: {
          id: number;
          fullName: string;
        };
        left: {
          id: number;
          fullName: string;
        };
        center: {
          id: number;
          fullName: string;
        };
        right: {
          id: number;
          fullName: string;
        };
        team: {
          id: number;
        };
        pitcher: {
          id: number;
          fullName: string;
        };
      };
    };
    boxscore: {
      teams: {
        away: {
          team: {
            id: number;
          };
          players: Players;
          battingOrder: number[];
          pitchers: number[];
        };
        home: {
          team: {
            id: number;
          };
          players: Players;
          battingOrder: number[];
          pitchers: number[];
        };
      };
    };
    plays: {
      allPlays: Play[];
      currentPlay: Play;
    };
  };
}

export interface Venue {
  id: number;
  name: string;
  timeZone: {
    id: string;
    offset: number;
    tz: string;
  };
}

interface TeamInformation {
  id: number;
  abbreviation: string;
}

const TeamIdAndAbbreviation: Array<TeamInformation> = [
  {
    id: 133,
    abbreviation: "OAK",
  },
  {
    id: 134,
    abbreviation: "PIT",
  },
  {
    id: 135,
    abbreviation: "SD",
  },
  {
    id: 136,
    abbreviation: "SEA",
  },
  {
    id: 137,
    abbreviation: "SF",
  },
  {
    id: 138,
    abbreviation: "STL",
  },
  {
    id: 139,
    abbreviation: "TB",
  },
  {
    id: 140,
    abbreviation: "TEX",
  },
  {
    id: 141,
    abbreviation: "TOR",
  },
  {
    id: 142,
    abbreviation: "MIN",
  },
  {
    id: 143,
    abbreviation: "PHI",
  },
  {
    id: 144,
    abbreviation: "ATL",
  },
  {
    id: 145,
    abbreviation: "CWS",
  },
  {
    id: 146,
    abbreviation: "MIA",
  },
  {
    id: 147,
    abbreviation: "NYY",
  },
  {
    id: 158,
    abbreviation: "MIL",
  },
  {
    id: 108,
    abbreviation: "LAA",
  },
  {
    id: 109,
    abbreviation: "ARI",
  },
  {
    id: 110,
    abbreviation: "BAL",
  },
  {
    id: 111,
    abbreviation: "BOS",
  },
  {
    id: 112,
    abbreviation: "CHC",
  },
  {
    id: 113,
    abbreviation: "CIN",
  },
  {
    id: 114,
    abbreviation: "CLE",
  },
  {
    id: 115,
    abbreviation: "COL",
  },
  {
    id: 116,
    abbreviation: "DET",
  },
  {
    id: 117,
    abbreviation: "HOU",
  },
  {
    id: 118,
    abbreviation: "KC",
  },
  {
    id: 119,
    abbreviation: "LAD",
  },
  {
    id: 120,
    abbreviation: "WSH",
  },
  {
    id: 121,
    abbreviation: "NYM",
  },
];

export const teamIdToAbbrev: {
  [key: number]: string;
} = TeamIdAndAbbreviation.reduce(
  (acc, team) => ({
    ...acc,
    [team.id]: team.abbreviation,
  }),
  {}
);

export const teamAbbrevToId: {
  [key: string]: number;
} = TeamIdAndAbbreviation.reduce(
  (acc, team) => ({
    ...acc,
    [team.abbreviation]: team.id,
  }),
  {}
);

export function skeletalFramesExtent(
  frames: SkeletalFrame[]
): [number, number] | undefined {
  if (frames.length == 0) return undefined;

  let start = +Infinity;
  let end = -Infinity;

  let prevTime = -Infinity;

  for (const frame of frames) {
    if (frame.time < prevTime) {
      console.error("non-monotonic frame buffer");
      return undefined;
    }
    if (frame.time < start) start = frame.time;
    if (frame.time > end) end = frame.time;

    prevTime = frame.time;
  }

  return [start, end];
}
